import React, { useEffect, useState } from "react";
import styled from "styled-components";

type NullableRecord = Record<string, any> | null | undefined;

export const MIN_SALE_CARD_SCALE = 0.45;
export const MAX_SALE_CARD_SCALE = 1.25;

export const clampSaleCardScale = (value: number): number => {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(value, MIN_SALE_CARD_SCALE), MAX_SALE_CARD_SCALE);
};

const DEFAULT_TRANSACTION_COLOR = '#6B21A8';

export const resolveTransactionColor = (...candidates: (string | undefined)[]): string => {
  const target = candidates
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .find((value) => value.length > 0);

  if (!target) return DEFAULT_TRANSACTION_COLOR;

  const lower = target.toLowerCase();
  if (lower.includes("전세")) return "#EF4444";
  if (lower.includes("매매")) return "#07BC0C";
  if (lower.includes("월세")) return "#003AFF";
  return DEFAULT_TRANSACTION_COLOR;
};

type SaleInfoCardProps = {
  info: NullableRecord;
  scale: number;
  showDescription?: boolean;
  className?: string;
};

export const SaleInfoCard: React.FC<SaleInfoCardProps> = ({
  info,
  scale,
  showDescription = true,
  className,
}) => {
  if (!info) return null;

  const safeScale = clampSaleCardScale(scale);
  const saleType = info?.saleType ?? "";
  const transactionType = info?.transactionType ?? "";
  const price = info?.price ?? "";
  const address = info?.address ?? "";
  const size = info?.size ?? "";
  const desc = info?.desc ?? "";
  const transactionColor = resolveTransactionColor(transactionType, saleType);

  return (
    <CardRoot className={className} $scale={safeScale}>
      <TitleRow $scale={safeScale}>
        {saleType?.trim() || "-"}
        {transactionType?.trim() && (
          <TransactionTag
            $scale={safeScale}
            style={{ backgroundColor: transactionColor }}
          >
            {transactionType}
          </TransactionTag>
        )}
      </TitleRow>

      {price?.trim() && (
        <Price $scale={safeScale}>
          {price}
        </Price>
      )}

      {address?.trim() && (
        <Address $scale={safeScale}>
          {address}
        </Address>
      )}

      {size?.trim() && (
        <Meta $scale={safeScale}>
          {size}
        </Meta>
      )}

      {showDescription && desc?.trim() && (
        <Description $scale={safeScale}>
          {desc}
        </Description>
      )}
    </CardRoot>
  );
};

export const useResponsiveScale = (
  ref: React.RefObject<HTMLElement>,
  baseWidth: number,
  options?: { min?: number; max?: number }
): number => {
  const [scale, setScale] = useState(1);
  const min = options?.min ?? MIN_SALE_CARD_SCALE;
  const max = options?.max ?? MAX_SALE_CARD_SCALE;

  useEffect(() => {
    const update = () => {
      const width = ref.current?.clientWidth;
      if (!width || width <= 0) {
        setScale(1);
        return;
      }
      const ratio = width / (baseWidth || 1);
      const bounded = Math.min(Math.max(ratio, min), max);
      const clamped = clampSaleCardScale(bounded);
      setScale(Number(clamped.toFixed(3)));
    };

    update();

    const element = ref.current;
    if (typeof ResizeObserver !== "undefined" && element) {
      const observer = new ResizeObserver(() => update());
      observer.observe(element);
      return () => observer.disconnect();
    }

    const handleResize = () => update();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [ref, baseWidth, min, max]);

  return scale;
};

const CardRoot = styled.div<{ $scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--card-bg, rgba(255, 255, 255, 0.96));
  border: 1px solid var(--card-border, #e6ecf5);
  padding: ${({ $scale }) => Math.round(18 * $scale + 10)}px;
  text-align: center;
  gap: ${({ $scale }) => Math.round(12 * $scale + 4)}px;
  color: var(--text-main, #0b1b2e);
  box-shadow: var(--card-shadow, 0 12px 40px rgba(18, 45, 90, 0.08));
`;

const TitleRow = styled.div<{ $scale: number }>`
  display: flex;
  align-items: center;
  gap: ${({ $scale }) => Math.round(14 * $scale)}px;
  font-weight: 800;
  font-size: ${({ $scale }) => (36 * $scale).toFixed(2)}px;
  line-height: 1.1;
  word-break: keep-all;
  justify-content: center;
  color: var(--text-main, #0b1b2e);
`;

const TransactionTag = styled.span<{ $scale: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border-radius: 999px;
  padding: ${({ $scale }) => Math.max(6, Math.round(10 * $scale))}px
    ${({ $scale }) => Math.max(12, Math.round(18 * $scale))}px;
  font-weight: 700;
  font-size: ${({ $scale }) => (28 * $scale).toFixed(2)}px;
  line-height: 1;
  white-space: nowrap;
`;

const Price = styled.div<{ $scale: number }>`
  font-weight: 900;
  font-size: ${({ $scale }) => (30 * $scale + 14).toFixed(2)}px;
  line-height: 1.05;
  color: var(--price-color, #0b1b2e);
  background-color: var(--price-bg, #facc15);
  padding: ${({ $scale }) => Math.max(6, Math.round(10 * $scale))}px
    ${({ $scale }) => Math.max(12, Math.round(18 * $scale))}px;
  border-radius: 12px;
  max-width: 100%;
  word-break: keep-all;
  white-space: pre-wrap;
  text-shadow: var(--price-shadow, none);
`;

const Address = styled.div<{ $scale: number }>`
  font-weight: 700;
  font-size: ${({ $scale }) => (32 * $scale).toFixed(2)}px;
  line-height: 1.3;
  color: var(--text-main, #2c4159);
  white-space: pre-wrap;
  word-break: keep-all;
`;

const Meta = styled.div<{ $scale: number }>`
  font-weight: 700;
  font-size: ${({ $scale }) => (30 * $scale).toFixed(2)}px;
  line-height: 1.25;
  color: var(--text-sub, #0b1b2e);
  white-space: pre-wrap;
`;

const Description = styled.div<{ $scale: number }>`
  font-weight: 600;
  font-size: ${({ $scale }) => (26 * $scale).toFixed(2)}px;
  line-height: 1.35;
  color: var(--text-sub, rgba(11, 27, 46, 0.78));
  white-space: pre-wrap;
  word-break: keep-all;
`;
